import { cloneElement, isValidElement, useEffect, useRef, useState } from 'react';
import type { CSSProperties, FC, FocusEvent, ReactElement, ReactNode } from 'react';
import { captureException } from '@sentry/react';
import { Button, Flex, Typography } from 'antd';
import { TbArrowBack, TbChevronDown, TbChevronUp, TbRefresh, TbX } from 'react-icons/tb';
import { Link } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { createPlatformStatusPalettes, PLATFORM_STATUS_ICONS } from '@/shared/components/alerts/platformStatus';
import { getCollapsibleBlockHoverRevealStyle } from '@/shared/components/layouts/hoverReveal';
import MiddleDot from '@/shared/components/typography/MiddleDot';
import { useUserStore } from '@/stores/database/entities/00-RBAC/01-Users/store';
import { formatPreferredTimezoneDateTime } from '@/utilities';

import { usePlatformToastPreferences } from './preferenceContext';
import type { PlatformToastAction, PlatformToastCardProps, PlatformToastLinkAction } from './types';

import './index.css';

const isPlatformToastAction = (action: PlatformToastAction | ReactNode): action is PlatformToastAction =>
    Boolean(
        action &&
        typeof action === 'object' &&
        !isValidElement(action) &&
        'label' in action &&
        ('href' in action || 'onClick' in action),
    );

const isPlatformToastLinkAction = (action: PlatformToastAction): action is PlatformToastLinkAction =>
    typeof action.href === 'string';

const normalizeIcon = (icon: ReactNode) =>
    isValidElement<{ style?: CSSProperties }>(icon)
        ? cloneElement(icon as ReactElement<{ style?: CSSProperties }>, {
              style: { ...icon.props.style, margin: 0, marginBlock: 0, marginInline: 0 },
          })
        : icon;

const PlatformToastCard: FC<PlatformToastCardProps> = ({
    actions = [],
    actionsAlwaysVisible = false,
    description,
    duration,
    icon,
    lane,
    metadata,
    onDismiss,
    status,
    style,
    timestamp,
    title,
    undo,
}) => {
    const { token } = useAntDesignTheme();
    const { bodyExpandedByDefault, onBodyExpandedPreferenceChange } = usePlatformToastPreferences();
    const bodyExpanded = bodyExpandedByDefault;
    const [actionsFocused, setActionsFocused] = useState(false);
    const [documentHidden, setDocumentHidden] = useState(
        () => typeof document !== 'undefined' && document.visibilityState === 'hidden',
    );
    const [hovered, setHovered] = useState(false);
    const undoInvokedRef = useRef(false);
    const preferredTimezone = useUserStore((state) => state.selectedUser?.timeZone);
    const paletteStatus = status === 'loading' ? 'info' : status;
    const palette = createPlatformStatusPalettes(token)[paletteStatus];
    const resolvedIcon = normalizeIcon(
        icon ??
            (status === 'loading' ? (
                <TbRefresh aria-label='Loading' className='platform-toast__loading-icon' />
            ) : (
                PLATFORM_STATUS_ICONS[status]
            )),
    );
    const effectiveUndoDuration = undo?.duration ?? duration;
    const hasFiniteDuration = Number.isFinite(effectiveUndoDuration) && effectiveUndoDuration > 0;
    const showExpiryFooter = Boolean(undo) || (status !== 'loading' && hasFiniteDuration);
    const showProgress = showExpiryFooter && hasFiniteDuration;
    const actionsVisible =
        bodyExpanded && Boolean(actions.length) && (actionsAlwaysVisible || hovered || actionsFocused);

    useEffect(() => {
        const syncDocumentVisibility = () => setDocumentHidden(document.visibilityState === 'hidden');

        document.addEventListener('visibilitychange', syncDocumentVisibility);

        return () => document.removeEventListener('visibilitychange', syncDocumentVisibility);
    }, []);

    const invokeUndo = async (event: Parameters<NonNullable<typeof undo>['onUndo']>[0]) => {
        if (!undo || undoInvokedRef.current) {
            return;
        }

        undoInvokedRef.current = true;
        onDismiss();

        try {
            await undo.onUndo(event);
        } catch (error) {
            captureException(error);
        }
    };

    const toggleBodyExpanded = () => {
        const nextExpanded = !bodyExpanded;

        onBodyExpandedPreferenceChange?.(nextExpanded);
    };

    const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setActionsFocused(false);
        }
    };

    const handleFocus = (event: FocusEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement;

        // Header controls retain focus after a pointer click. They should not
        // pin the hover-only actions open after the pointer leaves the card.
        setActionsFocused(target === event.currentTarget || Boolean(target.closest('.platform-toast__actions')));
    };

    return (
        <div
            className='platform-toast'
            data-actions-visible={actionsVisible}
            data-platform-toast-lane={lane}
            data-platform-toast-status={status}
            onBlurCapture={handleBlur}
            onFocusCapture={handleFocus}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            role={status === 'error' || status === 'warning' ? 'alert' : 'status'}
            style={
                {
                    '--platform-toast-motion-duration': token.motionDurationFast,
                    '--platform-toast-motion-easing': token.motionEaseInOut,
                    background: `linear-gradient(to right, ${palette.background} 0%, ${token.colorBgContainer} 100%) padding-box, linear-gradient(to right, ${palette.border} 0%, ${token.colorBorder} 100%) border-box`,
                    border: '1px solid transparent',
                    borderRadius: token.borderRadiusLG + 4,
                    boxShadow: token.boxShadowSecondary,
                    boxSizing: 'border-box',
                    color: token.colorText,
                    maxWidth: 'calc(100vw - 2rem)',
                    overflow: 'hidden',
                    position: 'relative',
                    width: lane === 'in-app' ? '27rem' : '24rem',
                    ...style,
                } as CSSProperties
            }
            tabIndex={actions.length && bodyExpanded ? 0 : undefined}
        >
            <div style={{ isolation: 'isolate', padding: '0.75rem', position: 'relative', zIndex: 1 }}>
                <Flex align='center' gap='0.625rem' wrap={false}>
                    <span
                        data-testid='platform-toast-icon'
                        style={{
                            alignItems: 'center',
                            background: `linear-gradient(${palette.iconBackground}, ${palette.iconBackground}), ${token.colorBgContainer}`,
                            border: `1px solid ${palette.border}`,
                            borderRadius: token.borderRadiusSM,
                            color: palette.accent,
                            display: 'inline-flex',
                            flex: '0 0 auto',
                            fontSize: '0.875rem',
                            height: '1.5rem',
                            justifyContent: 'center',
                            width: '1.5rem',
                        }}
                    >
                        {resolvedIcon}
                    </span>
                    <Typography.Text
                        strong
                        style={{
                            color: palette.title,
                            display: 'block',
                            flex: '1 1 auto',
                            lineHeight: 1.4,
                            minWidth: 0,
                        }}
                    >
                        {title}
                    </Typography.Text>
                    <Flex align='center' className='platform-toast__header-controls' gap='0.125rem' wrap={false}>
                        <Button
                            aria-expanded={bodyExpanded}
                            aria-label={bodyExpanded ? 'Collapse notification details' : 'Expand notification details'}
                            icon={bodyExpanded ? <TbChevronUp /> : <TbChevronDown />}
                            onClick={toggleBodyExpanded}
                            size='small'
                            style={{ color: token.colorTextTertiary, flex: '0 0 auto', height: '1.5rem' }}
                            type='text'
                        />
                        <Button
                            aria-label='Dismiss notification'
                            icon={<TbX />}
                            onClick={onDismiss}
                            size='small'
                            style={{ color: token.colorTextTertiary, flex: '0 0 auto', height: '1.5rem' }}
                            type='text'
                        />
                    </Flex>
                </Flex>
                <div
                    aria-hidden={!bodyExpanded}
                    className='platform-toast__collapsible'
                    data-collapsed={!bodyExpanded}
                    data-has-expiry-footer={showExpiryFooter}
                >
                    <div className='platform-toast__collapsible-inner'>
                        <div className='platform-toast__body' style={{ paddingInline: '0.75rem' }}>
                            <div style={{ marginInlineStart: '2.125rem', minWidth: 0 }}>
                                <div
                                    className='platform-toast__description'
                                    style={{
                                        color: palette.description,
                                        display: 'block',
                                        marginTop: '0.25rem',
                                        lineHeight: 1.5,
                                        overflowWrap: 'anywhere',
                                    }}
                                >
                                    {description}
                                </div>
                                {metadata ? (
                                    <div
                                        className='platform-toast__metadata'
                                        style={{
                                            color: token.colorTextSecondary,
                                            display: 'block',
                                            fontSize: token.fontSizeSM,
                                            marginTop: '0.25rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {metadata}
                                    </div>
                                ) : null}
                                <small
                                    className='platform-toast__timestamp'
                                    style={{
                                        color: token.colorTextTertiary,
                                        display: 'block',
                                        fontSize: token.fontSizeSM,
                                        lineHeight: 1.5,
                                        marginTop: '0.375rem',
                                    }}
                                >
                                    {formatPreferredTimezoneDateTime(timestamp, preferredTimezone, { month: 'short' })}
                                </small>
                                {actions.length ? (
                                    <Flex
                                        align='center'
                                        className='platform-toast__actions'
                                        gap={0}
                                        style={getCollapsibleBlockHoverRevealStyle(
                                            actionsVisible,
                                            token,
                                            { expandedMaxHeight: '6rem', gap: '0.5rem' },
                                            { flexWrap: 'wrap' },
                                        )}
                                        wrap='wrap'
                                    >
                                        {actions.map((action, index) => (
                                            <span key={index} style={{ alignItems: 'center', display: 'inline-flex' }}>
                                                {index ? <MiddleDot horizontalMargin='0.5rem' /> : null}
                                                {isPlatformToastAction(action) ? (
                                                    isPlatformToastLinkAction(action) ? (
                                                        <Link
                                                            aria-label={action.ariaLabel}
                                                            onClick={(event) => {
                                                                onDismiss();
                                                                void action.onClick?.(event);
                                                            }}
                                                            style={{ textDecoration: 'underline' }}
                                                            tabIndex={bodyExpanded ? 0 : -1}
                                                            to={action.href}
                                                        >
                                                            {action.label}
                                                        </Link>
                                                    ) : (
                                                        <Button
                                                            aria-label={action.ariaLabel}
                                                            onClick={action.onClick}
                                                            size='small'
                                                            style={{
                                                                height: 'auto',
                                                                textDecoration: 'underline',
                                                            }}
                                                            tabIndex={bodyExpanded ? 0 : -1}
                                                            type='link'
                                                        >
                                                            {action.label}
                                                        </Button>
                                                    )
                                                ) : (
                                                    action
                                                )}
                                            </span>
                                        ))}
                                    </Flex>
                                ) : null}
                            </div>
                        </div>
                        {showExpiryFooter ? (
                            <div
                                className='platform-toast__expiry-footer'
                                style={{
                                    background: token.colorFillQuaternary,
                                    borderTop: `1px solid ${token.colorBorderSecondary}`,
                                    boxSizing: 'border-box',
                                    isolation: 'isolate',
                                    marginTop: '0.75rem',
                                    minHeight: '2.25rem',
                                    padding: showProgress ? '0.5rem 0.75rem 0.625rem' : '0.5rem 0.75rem',
                                    position: 'relative',
                                    width: '100%',
                                    zIndex: 1,
                                }}
                            >
                                <Flex
                                    align='center'
                                    className='platform-toast__expiry-content'
                                    gap='0.75rem'
                                    justify={undo ? 'space-between' : 'flex-start'}
                                    wrap={false}
                                >
                                    <small
                                        className='platform-toast__expiry-message'
                                        style={{
                                            color: token.colorTextSecondary,
                                            minWidth: 0,
                                            overflow: 'hidden',
                                            textAlign: 'left',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            width: undo ? 'auto' : '100%',
                                        }}
                                    >
                                        {undo?.message ?? 'This notification will close automatically.'}
                                    </small>
                                    {undo ? (
                                        <Button
                                            onClick={(event) =>
                                                void invokeUndo(event as unknown as Parameters<typeof invokeUndo>[0])
                                            }
                                            size='small'
                                            style={{
                                                color: palette.accent,
                                                flex: '0 0 auto',
                                                height: 'auto',
                                                lineHeight: 1.5,
                                            }}
                                            tabIndex={bodyExpanded ? 0 : -1}
                                            type='link'
                                        >
                                            <small
                                                style={{ alignItems: 'center', display: 'inline-flex', gap: '0.25rem' }}
                                            >
                                                <TbArrowBack
                                                    aria-hidden='true'
                                                    size='1rem'
                                                    style={{ flex: '0 0 auto', margin: 0 }}
                                                />
                                                <span>{undo.label ?? 'Click to Undo'}</span>
                                            </small>
                                        </Button>
                                    ) : null}
                                </Flex>
                                {showProgress ? (
                                    <span
                                        aria-hidden='true'
                                        className='platform-toast__progress-track'
                                        data-testid='platform-toast-progress'
                                        style={{ background: token.colorBorderSecondary }}
                                    >
                                        <span
                                            className='platform-toast__progress'
                                            style={
                                                {
                                                    '--platform-toast-duration': `${effectiveUndoDuration}ms`,
                                                    animationPlayState: documentHidden ? 'paused' : undefined,
                                                    background: palette.accent,
                                                } as CSSProperties
                                            }
                                        />
                                    </span>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlatformToastCard;
